/**
 * @file legal-dto.ts
 * @description Контракт юридических документов магазина между API, кабинетом
 * и витриной (phase19.16, партия «Юридические документы» SB139–SB144; проект
 * данных docs/phases/phase19.16-materials/db-design-legal-docs.md §1.2, §1.3,
 * §4, §5.1): перечисления видов, DTO кабинета и витрины, коды ошибок и
 * предупреждений с русскими текстами по умолчанию.
 *
 * Зачем в shared: кабинет показывает текст ошибки по коду (`legal_file_encrypted`
 * → «Файл защищён паролем…»), витрина разбирает ответ по виду документа, а
 * API отвечает теми же формами. Расхождение = «Неизвестная ошибка» в кабинете
 * или пустая страница на витрине.
 *
 * Даты — ISO-строки (как во всех DTO пакета). Ответы API — `{ data }` либо
 * `{ error: { code, message, details? } }` (api-response.ts); `details` —
 * объект (у `legal_in_group_already` — `{ documentId, groupId, groupTitle }`).
 */

import { deepFreeze } from "../site-theme/deep-freeze.js";
import type { SiteMenuLocation } from "../site-theme/menu.js";

// ── Перечисления (= enum'ы Prisma) ──────────────────────────────────────────

/** SB139: вид документа. Задаётся при создании и не меняется. */
export const LEGAL_DOCUMENT_KINDS = deepFreeze(["TEXT", "PDF", "GROUP"] as const);
export type LegalDocumentKind = (typeof LEGAL_DOCUMENT_KINDS)[number];

/** Вид опубликованной редакции: у группы редакций нет. */
export type LegalRevisionKind = Exclude<LegalDocumentKind, "GROUP">;

/** SB139: как страница PDF-документа показывает файл (на адрес файла не влияет). */
export const LEGAL_PDF_DISPLAYS = deepFreeze(["INLINE", "DOWNLOAD"] as const);
export type LegalPdfDisplay = (typeof LEGAL_PDF_DISPLAYS)[number];

/** SB141: откуда группа берёт состав. */
export const LEGAL_GROUP_SOURCES = deepFreeze(["MANUAL", "PRODUCER_CERTIFICATES"] as const);
export type LegalGroupSource = (typeof LEGAL_GROUP_SOURCES)[number];

/** Источник редакции (`SiteRevisionKind`): «Опубликовать», перенос при вводе редакций, восстановление. */
export const LEGAL_REVISION_SOURCES = deepFreeze(["PUBLISH", "ROLLBACK", "RESTORE", "IMPORT"] as const);
export type LegalRevisionSource = (typeof LEGAL_REVISION_SOURCES)[number];

/** Подписи видов для кабинета (окно «Новый документ», пометка в списках). */
export const LEGAL_DOCUMENT_KIND_LABELS: Readonly<Record<LegalDocumentKind, string>> = deepFreeze({
  TEXT: "Текст",
  PDF: "PDF-файл",
  GROUP: "Группа документов",
});

/** Пояснения к видам в окне «Новый документ» (uiux-legal-docs §2). */
export const LEGAL_DOCUMENT_KIND_HINTS: Readonly<Record<LegalDocumentKind, string>> = deepFreeze({
  TEXT: "Пишете в редакторе. Оферта и политика ПДн — только так",
  PDF: "Готовый файл: сертификат, декларация, лицензия",
  GROUP: "Страница-список из нескольких документов",
});

export const LEGAL_PDF_DISPLAY_LABELS: Readonly<Record<LegalPdfDisplay, string>> = deepFreeze({
  INLINE: "Показывать в браузере",
  DOWNLOAD: "Сразу скачивать",
});

// ── Состояние (вычисляется, legal-state.ts) ─────────────────────────────────

/**
 * Состояние в кабинете (§1.3): черновик, опубликован, опубликован с правками,
 * снят с публикации (редакции сохранены), группа опубликована, но пуста на
 * сайте (SB16).
 */
export const LEGAL_DOCUMENT_STATUSES = deepFreeze(["draft", "published", "changed", "unpublished", "empty"] as const);
export type LegalDocumentStatus = (typeof LEGAL_DOCUMENT_STATUSES)[number];

// ── Коды ошибок (§3.3, §4, §5.1) ────────────────────────────────────────────

/** Коды ошибок API документов. */
export const LEGAL_ERROR_CODES = deepFreeze([
  "legal_document_not_found",
  "legal_revision_not_found",
  "legal_slug_invalid",
  "legal_slug_taken",
  "legal_slug_reserved",
  "legal_offer_text_only",
  "legal_certificates_group_exists",
  "legal_kind_immutable",
  "legal_field_not_for_kind",
  "legal_kind_mismatch",
  "legal_draft_changed",
  "legal_file_too_large",
  "legal_file_not_pdf",
  "legal_file_broken",
  "legal_file_encrypted",
  "legal_file_missing",
  "legal_draft_file_missing",
  "legal_pdf_required",
  "legal_description_required",
  "legal_text_empty",
  "legal_group_empty",
  "legal_nothing_to_publish",
  "legal_publish_conflict",
  "legal_no_live_revision",
  "legal_not_a_group",
  "legal_group_in_group",
  "legal_group_automatic",
  "legal_member_not_found",
  "legal_member_duplicate",
  "legal_in_group_already",
  "legal_delete_has_revisions",
  "storage_unavailable",
] as const);
export type LegalErrorCode = (typeof LEGAL_ERROR_CODES)[number];

/** HTTP-статус по коду — один для API и тестов кабинета. */
export const LEGAL_ERROR_HTTP_STATUS: Readonly<Record<LegalErrorCode, number>> = deepFreeze({
  legal_document_not_found: 404,
  legal_revision_not_found: 404,
  legal_slug_invalid: 400,
  legal_slug_taken: 409,
  legal_slug_reserved: 422,
  legal_offer_text_only: 422,
  legal_certificates_group_exists: 422,
  legal_kind_immutable: 422,
  legal_field_not_for_kind: 422,
  legal_kind_mismatch: 422,
  legal_draft_changed: 409,
  legal_file_too_large: 413,
  legal_file_not_pdf: 422,
  legal_file_broken: 422,
  legal_file_encrypted: 422,
  legal_file_missing: 404,
  legal_draft_file_missing: 409,
  legal_pdf_required: 422,
  legal_description_required: 422,
  legal_text_empty: 422,
  legal_group_empty: 422,
  legal_nothing_to_publish: 409,
  legal_publish_conflict: 409,
  legal_no_live_revision: 409,
  legal_not_a_group: 422,
  legal_group_in_group: 422,
  legal_group_automatic: 422,
  legal_member_not_found: 422,
  legal_member_duplicate: 400,
  legal_in_group_already: 409,
  legal_delete_has_revisions: 409,
  storage_unavailable: 502,
});

/** Тексты по умолчанию (uiux-legal-docs §7) — безопасно показывать сотруднику. */
export const LEGAL_ERROR_MESSAGES: Readonly<Record<LegalErrorCode, string>> = deepFreeze({
  legal_document_not_found: "Документ не найден — возможно, его удалили.",
  legal_revision_not_found: "Такой редакции у документа нет.",
  legal_slug_invalid:
    "Адрес — латиница в нижнем регистре, цифры и дефисы (без дефиса в начале и в конце), до 60 знаков.",
  legal_slug_taken: "Этот адрес уже занят другим документом или группой — выберите другой.",
  legal_slug_reserved: "Этот адрес занят служебной страницей сайта — выберите другой.",
  legal_offer_text_only: "Оферта и политика ПДн бывают только текстом — PDF и группа для них недоступны.",
  legal_certificates_group_exists: "Группа сертификатов уже есть — она одна на магазин.",
  legal_kind_immutable: "Вид документа выбирается один раз при создании и не меняется.",
  legal_field_not_for_kind: "Это поле не относится к документу такого вида.",
  legal_kind_mismatch: "Файл можно загрузить только в документ вида «PDF-файл».",
  legal_draft_changed: "Документ только что изменил другой сотрудник — обновите страницу, чтобы не потерять правки.",
  legal_file_too_large: "Файл больше 20 МБ — уменьшите или разделите его.",
  legal_file_not_pdf: "Можно загрузить только PDF.",
  legal_file_broken: "Файл не открывается как PDF — сохраните его заново и загрузите.",
  legal_file_encrypted: "Файл защищён паролем — сохраните его без пароля и загрузите снова.",
  legal_file_missing: "Файл документа не найден.",
  legal_draft_file_missing: "Нового файла в черновике нет — убирать нечего.",
  legal_pdf_required: "Загрузите файл — без него документ нельзя опубликовать.",
  legal_description_required: "Добавьте краткое описание — без него документ нельзя опубликовать.",
  legal_text_empty: "Текст документа пуст — напишите текст, чтобы опубликовать.",
  legal_group_empty: "Добавьте хотя бы один опубликованный документ — пустую группу нельзя опубликовать.",
  legal_nothing_to_publish: "Правок нет — на сайте уже действующая редакция.",
  legal_publish_conflict: "Документ публикуют одновременно — обновите страницу и повторите.",
  legal_no_live_revision: "У документа ещё нет опубликованной редакции — отменять нечего.",
  legal_not_a_group: "Состав задаётся только у группы документов.",
  legal_group_in_group: "Группу нельзя добавить в другую группу.",
  legal_group_automatic: "Состав этой группы собирается автоматически из сертификатов производителей.",
  legal_member_not_found: "Один из документов не найден — обновите страницу.",
  legal_member_duplicate: "Документ указан в группе дважды.",
  legal_in_group_already: "Документ уже входит в другую группу — документ бывает только в одной группе.",
  legal_delete_has_revisions:
    "У документа есть опубликованные редакции — удалить его нельзя. Снимите документ с публикации.",
  storage_unavailable: "Хранилище файлов временно недоступно — попробуйте позже.",
});

/** `details` у 409 `legal_draft_changed`: кто и когда сохранил последним. */
export interface LegalDraftChangedDetails {
  version: number;
  updatedBy: string | null;
  updatedAt: string;
}

/** `details` у 409 `legal_in_group_already` (без `move`). */
export interface LegalInGroupAlreadyDetails {
  documentId: string;
  groupId: string;
  groupTitle: string;
}

// ── Предупреждения (не запреты, SB120) ──────────────────────────────────────

/**
 * Предупреждения документа (§4): обязательный документ в виде PDF (SB140),
 * нет описания, в группе есть неопубликованные документы, группа пуста на
 * сайте; при снятии — обязательный документ снят (плашка SB125), группа
 * станет пустой; шаг «Сертификаты» — просроченные сертификаты.
 */
export const LEGAL_WARNING_CODES = deepFreeze([
  "legal_required_doc_pdf",
  "legal_description_missing",
  "legal_member_not_published",
  "legal_group_hidden_empty",
  "legal_required_doc_unpublished",
  "legal_group_becomes_empty",
  "legal_certificates_expired",
] as const);
export type LegalWarningCode = (typeof LEGAL_WARNING_CODES)[number];

/** Предупреждение: код, число (сколько документов) и группа (у `legal_group_becomes_empty`). */
export interface LegalDocumentWarning {
  code: LegalWarningCode;
  count?: number;
  group?: { id: string; title: string };
}

export const LEGAL_WARNING_MESSAGES: Readonly<Record<LegalWarningCode, string>> = deepFreeze({
  legal_required_doc_pdf:
    "Обязательный документ лучше публиковать текстом: текст читается на телефоне и находится поиском.",
  legal_description_missing: "Нет краткого описания — без него документ нельзя опубликовать.",
  legal_member_not_published: "В группе есть неопубликованные документы — на сайте их не видно.",
  legal_group_hidden_empty: "В группе нет опубликованных документов — на сайте она не видна.",
  legal_required_doc_unpublished: "Без оферты и политики ПДн сайт работает с нарушением закона — опубликуйте документ.",
  legal_group_becomes_empty: "Группа станет пустой и пропадёт с сайта.",
  legal_certificates_expired: "Есть просроченные сертификаты — на сайте они не показываются.",
});

// ── DTO кабинета `/admin/shop/legal-documents` (§4) ────────────────────────

/** Действующая редакция коротко. */
export interface LegalLiveRevisionInfoDto {
  number: number;
  publishedAt: string;
  publishedByName: string;
}

/** Файл PDF: новый файл черновика или файл действующей редакции. */
export interface LegalFileInfoDto {
  name: string;
  sizeBytes: number;
  source: "draft" | "live";
}

/** Ссылка на группу. */
export interface LegalGroupRefDto {
  id: string;
  title: string;
}

/** Строка списка документов. `contentHtml` в список не входит. */
export interface LegalDocumentListItemDto {
  id: string;
  slug: string;
  kind: LegalDocumentKind;
  title: string;
  /** Адрес на сайте (`legalDocumentHref`). */
  href: string;
  status: LegalDocumentStatus;
  isPublished: boolean;
  hasDraftChanges: boolean;
  /** Виден ли покупателю сейчас (`isLegalDocumentVisible`). */
  visibleOnSite: boolean;
  live: LegalLiveRevisionInfoDto | null;
  file: LegalFileInfoDto | null;
  group: LegalGroupRefDto | null;
  /** Только GROUP. */
  members: { total: number; visible: number } | null;
  groupSource: LegalGroupSource | null;
  warnings: LegalDocumentWarning[];
}

/** Фильтр списка (`?status=&kind=&q=`). */
export const LEGAL_LIST_STATUS_FILTERS = deepFreeze(["all", "published", "draft"] as const);
export type LegalListStatusFilter = (typeof LEGAL_LIST_STATUS_FILTERS)[number];

export interface LegalDocumentListQuery {
  status?: LegalListStatusFilter;
  kind?: LegalDocumentKind;
  q?: string;
}

/** Документ в составе группы (карточка группы). */
export interface LegalGroupMemberDto {
  id: string;
  slug: string;
  title: string;
  kind: LegalRevisionKind;
  status: LegalDocumentStatus;
  visibleOnSite: boolean;
  position: number;
}

/** Пункт меню, ведущий на документ («Где используется», окна удаления и снятия). */
export interface LegalUsageMenuItemDto {
  id: string;
  label: string;
  location: SiteMenuLocation;
}

/** «Где используется» (uiux-legal-docs §7). */
export interface LegalDocumentUsageDto {
  menuItems: LegalUsageMenuItemDto[];
  /** Свои строки «Ссылок» подвала: в черновике темы и в опубликованной ревизии. */
  footerRows: { draft: boolean; published: boolean };
  group: LegalGroupRefDto | null;
}

/** Файл черновика (все поля вместе — CHECK). */
export interface LegalDraftFileDto {
  name: string;
  sizeBytes: number;
  sha256: string;
  uploadedAt: string;
}

/** Карточка документа: черновик целиком, действующая редакция, состав, «где используется». */
export interface LegalDocumentDto {
  id: string;
  slug: string;
  kind: LegalDocumentKind;
  title: string;
  description: string | null;
  /** TEXT — черновик текста; PDF и GROUP — null. */
  contentHtml: string | null;
  pdfDisplay: LegalPdfDisplay;
  /** Новый файл черновика; null — файл = файл действующей редакции (или файла ещё нет). */
  draftFile: LegalDraftFileDto | null;
  /** Файл, который уйдёт в редакцию при публикации (черновика или действующей). */
  file: LegalFileInfoDto | null;
  groupSource: LegalGroupSource | null;
  group: LegalGroupRefDto | null;
  groupPosition: number | null;
  href: string;
  status: LegalDocumentStatus;
  isPublished: boolean;
  hasDraftChanges: boolean;
  visibleOnSite: boolean;
  /** Счётчик сохранений черновика — `expectedVersion` в PATCH/publish/discard. На сайте не показывается. */
  version: number;
  publishedAt: string | null;
  live: LegalLiveRevisionInfoDto | null;
  /** Только GROUP, по порядку. */
  members: LegalGroupMemberDto[] | null;
  usage: LegalDocumentUsageDto;
  warnings: LegalDocumentWarning[];
  createdAt: string;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}

/** POST `/` — вид фиксируется. TEXT без текста — пустая строка. */
export interface LegalDocumentCreateRequest {
  kind: LegalDocumentKind;
  slug: string;
  title: string;
  description?: string | null;
  contentHtml?: string | null;
  /** Только GROUP; по умолчанию MANUAL. */
  groupSource?: LegalGroupSource | null;
}

/** PATCH `/:id` — только черновик (у GROUP — сразу на сайте). */
export interface LegalDocumentUpdateRequest {
  expectedVersion?: number;
  title?: string;
  description?: string | null;
  contentHtml?: string;
  pdfDisplay?: LegalPdfDisplay;
}

/** POST `/:id/publish` и `/:id/discard`. */
export interface LegalVersionedRequest {
  expectedVersion: number;
}

/** Редакция в истории (`GET /:id/revisions`). */
export interface LegalRevisionListItemDto {
  number: number;
  source: LegalRevisionSource;
  title: string;
  publishedAt: string;
  publishedByName: string;
  file: { name: string; sizeBytes: number } | null;
  note: string | null;
}

/** Страница истории: `nextBefore` — номер для `?before=`; null — дальше нет. */
export interface LegalRevisionsPageDto {
  items: LegalRevisionListItemDto[];
  nextBefore: number | null;
}

/** Содержимое редакции (`GET /:id/revisions/:number`). */
export interface LegalRevisionDto extends LegalRevisionListItemDto {
  kind: LegalRevisionKind;
  slug: string;
  description: string | null;
  /** TEXT — текст редакции; PDF — null. */
  contentHtml: string | null;
  contentSha256: string;
  isLive: boolean;
}

/** Ответ публикации: редакция null — показан снова без новой редакции (или GROUP). */
export interface LegalPublishResultDto {
  document: LegalDocumentDto;
  revision: LegalRevisionListItemDto | null;
  warnings: LegalDocumentWarning[];
}

/** Ответ снятия с публикации. */
export interface LegalUnpublishResultDto {
  document: LegalDocumentDto;
  warnings: LegalDocumentWarning[];
}

/** PUT `/:groupId/members` — весь упорядоченный состав; `move` — забрать из других групп. */
export interface LegalGroupMembersRequest {
  documentIds: string[];
  move?: boolean;
}

// ── Публичное API `/public/legal` (§5.1) ────────────────────────────────────

/** Редакция на витрине коротко. */
export interface PublicLegalRevisionRefDto {
  number: number;
  publishedAt: string;
}

/** Документ в списке «Все документы» и в группе. */
export interface PublicLegalItemDto {
  slug: string;
  href: string;
  kind: LegalRevisionKind;
  title: string;
  description: string | null;
  revision: PublicLegalRevisionRefDto;
  /** Только PDF: размер для подписи «PDF, 2,4 МБ». */
  file: { sizeBytes: number } | null;
}

/**
 * Сертификат автоматической группы (SB141, §7): действующий и публичный
 * сертификат производителя с файлом. `validUntil` null — бессрочный (СГР,
 * решение оркестратора 2026-10-19). Адреса файла — через API
 * (`legalCertificateFileHref`, `?download=1`).
 */
export interface PublicLegalCertificateDto {
  id: string;
  producerId: string;
  producerName: string;
  /** Вид сертификата (значение справочника). */
  type: string;
  /** Подпись вида для покупателя. */
  typeLabel: string;
  number: string;
  issuedBy: string | null;
  /** Дата «действует до» (ISO); null — бессрочный. */
  validUntil: string | null;
  fileHref: string;
  downloadHref: string;
  fileSizeBytes: number | null;
  fileMimeType: string | null;
}

/** Группа на странице «Все документы». */
export interface PublicLegalGroupDto {
  slug: string;
  /** Адрес страницы группы (`/legal/<адрес>`). */
  href: string;
  title: string;
  description: string | null;
  /** Ручная группа или автоматическая группа сертификатов. */
  groupSource: LegalGroupSource;
  /** Документы ручной группы по порядку (у группы сертификатов — пусто). */
  documents: PublicLegalItemDto[];
  /** Только группа сертификатов; у ручной — нет или null. */
  certificates?: PublicLegalCertificateDto[] | null;
}

/** GET `/public/legal`: сначала группы, потом документы без группы (SB141); только видимые. */
export interface PublicLegalIndexDto {
  groups: PublicLegalGroupDto[];
  documents: PublicLegalItemDto[];
}

/** Файл PDF на витрине: адреса через API (`legalFileHref`). */
export interface PublicLegalFileDto {
  href: string;
  downloadHref: string;
  sizeBytes: number;
}

/** Общие поля документа на витрине. */
interface PublicLegalDocumentBase {
  slug: string;
  href: string;
  title: string;
  description: string | null;
  /** Крошки «Главная › Группа › …»; null — документ без группы. */
  group: { slug: string; href: string; title: string } | null;
  /** Есть ли прежние редакции — ссылка «Предыдущие редакции». */
  hasPreviousRevisions: boolean;
}

/** TEXT: текст действующей редакции. `version`/`publishedAt` — совместимость со старой формой. */
export interface PublicLegalTextDocumentDto extends PublicLegalDocumentBase {
  kind: "TEXT";
  revision: PublicLegalRevisionRefDto;
  version: number;
  publishedAt: string;
  contentHtml: string;
  file: null;
  pdfDisplay: null;
  documents: null;
}

/** PDF: файл действующей редакции и способ показа. */
export interface PublicLegalPdfDocumentDto extends PublicLegalDocumentBase {
  kind: "PDF";
  revision: PublicLegalRevisionRefDto;
  version: number;
  publishedAt: string;
  contentHtml: null;
  file: PublicLegalFileDto;
  pdfDisplay: LegalPdfDisplay;
  documents: null;
}

/** GROUP: страница-список, редакций нет. */
export interface PublicLegalGroupDocumentDto extends PublicLegalDocumentBase {
  kind: "GROUP";
  revision: null;
  version: null;
  publishedAt: string | null;
  contentHtml: null;
  file: null;
  pdfDisplay: null;
  groupSource: LegalGroupSource;
  documents: PublicLegalItemDto[];
  /** Только группа сертификатов; у ручной — нет или null. */
  certificates?: PublicLegalCertificateDto[] | null;
}

/** GET `/public/legal/:slug` — по виду документа. */
export type PublicLegalDocumentDto = PublicLegalTextDocumentDto | PublicLegalPdfDocumentDto | PublicLegalGroupDocumentDto;

/**
 * Источники, которые сейчас пишет код: «Опубликовать» и перенос при вводе
 * редакций (у перенесённой — пометка «ранние редакции не сохранялись»).
 * Поле DTO принимает весь `SiteRevisionKind` (`LegalRevisionSource`): api
 * отдаёт колонку как есть, а ROLLBACK/RESTORE витрина показывает как обычную
 * публикацию — так добавление значения в enum не ломает типы.
 */
export const PUBLIC_LEGAL_REVISION_SOURCES = deepFreeze(["PUBLISH", "IMPORT"] as const);
export type PublicLegalRevisionSource = (typeof PUBLIC_LEGAL_REVISION_SOURCES)[number];

/** Строка «Предыдущих редакций» (`GET /public/legal/:slug/revisions`). */
export interface PublicLegalRevisionListItemDto {
  number: number;
  title: string;
  publishedAt: string;
  isCurrent: boolean;
  source: LegalRevisionSource;
}

/** Прежняя редакция (`GET /public/legal/:slug/revisions/:number`), noindex. */
export interface PublicLegalRevisionDto {
  slug: string;
  kind: LegalRevisionKind;
  number: number;
  /** Необязательно: добавлено после первой версии ответа api. */
  source?: LegalRevisionSource;
  title: string;
  description: string | null;
  publishedAt: string;
  isCurrent: boolean;
  /** TEXT — текст редакции; PDF — null. */
  contentHtml: string | null;
  /** PDF — файл этой редакции; TEXT — null. */
  file: PublicLegalFileDto | null;
}
